import { BlockStack, Box } from "@shopify/polaris";
import { CSSProperties, MouseEvent as ReactMouseEvent, useMemo, useState } from "react";
import { StorefrontSearchReport } from "../../app/models/Reports";

type DailySearchPoint = {
    date: string;
    label: string;
    count: number;
    available: boolean;
};

type SearchActivityChartProps = {
    report?: StorefrontSearchReport;
    days?: number;
    compact?: boolean;
    embedded?: boolean;
    showComparison?: boolean;
    variant?: "bar" | "line";
    inProgressDate?: string;
};

type ChartAxisScale = {
    maximum: number;
    ticks: number[];
};

export const formatSearchCount = (value?: number) => (value ?? 0).toLocaleString();

export const formatSearchDateLabel = (value?: string) => {
    if (!value) return "-";

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);

    return parsed.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
};

const formatSearchTooltipDate = (value?: string) => {
    if (!value) return "-";

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);

    return parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const formatSearchLegendDate = (value?: string) => {
    if (!value) return "-";

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);

    return parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const toLocalInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const buildDailySearchTrend = (
    dailyCounts: StorefrontSearchReport["dailyCounts"] | undefined,
    rangeEndValue: string | undefined,
    requestedDays = 14,
    dataAvailableFromValue?: string | null
): DailySearchPoint[] => {
    if (!rangeEndValue) return [];

    const rangeEnd = new Date(`${rangeEndValue.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(rangeEnd.getTime())) return [];

    const numberOfDays = Math.max(1, requestedDays);
    const countsByDate = new Map(
        (Array.isArray(dailyCounts) ? dailyCounts : [])
            .filter((day) => Boolean(day?.date))
            .map((day) => [day.date.slice(0, 10), Number(day.searchCount) || 0])
    );
    const dataAvailableFrom = dataAvailableFromValue?.slice(0, 10);
    const hasAvailabilityMetadata = dataAvailableFromValue !== undefined;

    return Array.from({ length: numberOfDays }, (_, index) => {
        const date = new Date(rangeEnd);
        date.setDate(rangeEnd.getDate() - (numberOfDays - index - 1));
        const dateKey = toLocalInputDate(date);

        return {
            date: dateKey,
            label: formatSearchDateLabel(dateKey),
            count: countsByDate.get(dateKey) ?? 0,
            available: hasAvailabilityMetadata
                && Boolean(dataAvailableFrom && dateKey >= dataAvailableFrom),
        };
    });
};

const buildChartAxisScale = (maximumValue: number): ChartAxisScale => {
    if (maximumValue <= 0) {
        return {
            maximum: 4,
            ticks: [0, 1, 2, 3, 4],
        };
    }

    const targetIntervals = 4;
    const paddedMaximum = maximumValue * 1.1;
    const rawStep = maximumValue / targetIntervals;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const normalizedStep = rawStep / magnitude;
    const niceMultipliers = [1, 1.5, 2, 2.5, 5, 10];
    const niceMultiplier = niceMultipliers.find((value) => normalizedStep <= value) ?? 10;
    // Search counts are whole numbers, so axis ticks should never introduce
    // fractional searches when activity is low.
    const step = Math.max(1, niceMultiplier * magnitude);
    const intervalCount = Math.max(targetIntervals, Math.ceil(paddedMaximum / step));
    const axisMaximum = step * intervalCount;

    return {
        maximum: axisMaximum,
        ticks: Array.from({ length: intervalCount + 1 }, (_, index) => index * step),
    };
};

const buildLinePath = (points: DailySearchPoint[], maximum: number) => {
    if (points.length === 0 || maximum <= 0) return "";

    let path = "";
    let previous: { x: number; y: number } | undefined;

    points.forEach((point, index) => {
        if (!point.available) {
            previous = undefined;
            return;
        }

        const coordinate = {
            x: ((index + 0.5) / points.length) * 100,
            y: 100 - (point.count / maximum) * 100,
        };

        if (!previous) {
            path += `${path ? " " : ""}M ${coordinate.x} ${coordinate.y}`;
        } else {
            const midpoint = (previous.x + coordinate.x) / 2;
            path += ` C ${midpoint} ${previous.y}, ${midpoint} ${coordinate.y}, ${coordinate.x} ${coordinate.y}`;
        }

        previous = coordinate;
    });

    return path;
};

const getInterpolatedChartHeight = (
    points: DailySearchPoint[],
    index: number,
    positionWithinColumn: number,
    maximum: number
) => {
    if (points.length === 0 || maximum <= 0 || !points[index]?.available) return null;

    let startIndex = index;
    let endIndex = index;
    let progress = 0;

    if (positionWithinColumn < 0.5 && index > 0 && points[index - 1].available) {
        startIndex = index - 1;
        endIndex = index;
        progress = positionWithinColumn + 0.5;
    } else if (
        positionWithinColumn >= 0.5
        && index < points.length - 1
        && points[index + 1].available
    ) {
        startIndex = index;
        endIndex = index + 1;
        progress = positionWithinColumn - 0.5;
    }

    const easedProgress = progress * progress * (3 - 2 * progress);
    const interpolatedCount = points[startIndex].count
        + (points[endIndex].count - points[startIndex].count) * easedProgress;
    return (interpolatedCount / maximum) * 100;
};

const formatComparisonChange = (current: number, comparison: number) => {
    if (comparison === 0) {
        if (current === 0) return { label: "No change from comparison", tone: "neutral" };
        return { label: "New from comparison", tone: "positive" };
    }

    const percent = Math.round(((current - comparison) / comparison) * 100);
    if (percent === 0) return { label: "No change from comparison", tone: "neutral" };

    return {
        label: `${percent > 0 ? "↗" : "↘"} ${Math.abs(percent)}% from comparison`,
        tone: percent > 0 ? "positive" : "negative",
    };
};

const formatOverallComparison = (current: number, comparison: number) => {
    if (comparison === 0) {
        if (current === 0) {
            return {
                accessibleLabel: "No change from comparison period",
                direction: "",
                label: "0%",
                tone: "neutral",
            };
        }

        return {
            accessibleLabel: "New activity compared with comparison period",
            direction: "↗",
            label: "New",
            tone: "positive",
        };
    }

    const percent = Math.round(((current - comparison) / comparison) * 100);
    if (percent === 0) {
        return {
            accessibleLabel: "No change from comparison period",
            direction: "",
            label: "0%",
            tone: "neutral",
        };
    }

    return {
        accessibleLabel: `${Math.abs(percent)} percent ${percent > 0 ? "increase" : "decrease"} from comparison period`,
        direction: percent > 0 ? "↗" : "↘",
        label: `${Math.abs(percent)}%`,
        tone: percent > 0 ? "positive" : "negative",
    };
};

export const SearchActivityChart = ({
    report,
    days = 14,
    compact = false,
    embedded = false,
    showComparison = false,
    variant = "bar",
    inProgressDate,
}: SearchActivityChartProps) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [hoveredSeries, setHoveredSeries] = useState<"current" | "comparison" | null>(null);
    const dailySearchTrend = useMemo(
        () => buildDailySearchTrend(
            report?.dailyCounts,
            report?.to,
            days,
            report?.dataAvailableFrom
        ),
        [days, report]
    );
    const comparisonSearchTrend = useMemo(
        () => showComparison && report?.comparisonEnabled
            ? buildDailySearchTrend(
                report.previousDailyCounts,
                report.previousTo,
                days,
                report.dataAvailableFrom
            )
            : [],
        [days, report, showComparison]
    );
    const comparisonVisible = comparisonSearchTrend.some((day) => day.available);
    const dataAvailableFrom = report?.dataAvailableFrom?.slice(0, 10);
    const comparisonDataComplete = !report?.comparisonEnabled
        || (
            report.dataAvailableFrom !== undefined
            && Boolean(
                dataAvailableFrom
                && report.previousFrom
                && report.previousFrom.slice(0, 10) >= dataAvailableFrom
            )
        );
    const highestDailySearchCount = Math.max(
        0,
        ...dailySearchTrend.filter((day) => day.available).map((day) => day.count),
        ...comparisonSearchTrend.filter((day) => day.available).map((day) => day.count)
    );
    const axisScale = useMemo(
        () => buildChartAxisScale(highestDailySearchCount),
        [highestDailySearchCount]
    );
    const xAxisLabelInterval = Math.max(
        1,
        Math.ceil(Math.max(1, dailySearchTrend.length - 1) / (compact ? 6 : 5))
    );
    const columnsStyle = {
        "--pf-chart-column-count": dailySearchTrend.length,
    } as CSSProperties;
    const chartDensityClass = dailySearchTrend.length <= 14
        ? " pf-search-activity-chart--sparse"
        : "";
    const isLineChart = variant === "line";
    const currentLinePath = useMemo(
        () => buildLinePath(dailySearchTrend.map(day => ({ ...day, available: day.available && day.date !== inProgressDate })), axisScale.maximum),
        [axisScale.maximum, dailySearchTrend, inProgressDate]
    );
    const inProgressLinePath = useMemo(() => {
        const index = dailySearchTrend.findIndex(day => day.date === inProgressDate && day.available);
        return index < 0 ? '' : buildLinePath(dailySearchTrend.map((day, dayIndex) => ({
            ...day, available: day.available && (dayIndex === index || dayIndex === index - 1),
        })), axisScale.maximum);
    }, [axisScale.maximum, dailySearchTrend, inProgressDate]);
    const comparisonLinePath = useMemo(
        () => buildLinePath(comparisonSearchTrend, axisScale.maximum),
        [axisScale.maximum, comparisonSearchTrend]
    );
    const visibleSearchTotal = useMemo(
        () => dailySearchTrend.reduce(
            (total, day) => total + (day.available ? day.count : 0),
            0
        ),
        [dailySearchTrend]
    );
    const visibleComparisonTotal = useMemo(
        () => comparisonSearchTrend.reduce(
            (total, day) => total + (day.available ? day.count : 0),
            0
        ),
        [comparisonSearchTrend]
    );
    const overallComparison = comparisonVisible && comparisonDataComplete
        ? formatOverallComparison(visibleSearchTotal, visibleComparisonTotal)
        : undefined;
    const hoveredDay = hoveredIndex === null
        ? undefined
        : dailySearchTrend[hoveredIndex];
    const hoveredComparisonDay = hoveredIndex === null
        ? undefined
        : comparisonSearchTrend[hoveredIndex];
    const hoveredHeight = hoveredDay?.available
        ? (hoveredDay.count / axisScale.maximum) * 100
        : 0;
    const hoveredComparisonHeight = hoveredComparisonDay?.available
        ? (hoveredComparisonDay.count / axisScale.maximum) * 100
        : 0;
    const hoveredTooltipHeight = Math.max(hoveredHeight, hoveredComparisonHeight);
    const hoveredComparisonChange = hoveredDay?.available && hoveredComparisonDay?.available && hoveredDay.date !== inProgressDate
        ? formatComparisonChange(hoveredDay.count, hoveredComparisonDay.count)
        : undefined;
    const hoverIndicatorStyle = hoveredIndex !== null
        && (hoveredDay?.available || hoveredComparisonDay?.available)
        ? {
            "--pf-chart-hover-x": `${((hoveredIndex + 0.5) / dailySearchTrend.length) * 100}%`,
            "--pf-chart-current-point-height": `${hoveredHeight}%`,
            "--pf-chart-comparison-point-height": `${hoveredComparisonHeight}%`,
            "--pf-chart-tooltip-height": `${hoveredTooltipHeight}%`,
        } as CSSProperties
        : undefined;
    const lineTooltipAlignment = hoveredIndex === 0
        ? " pf-search-activity-chart__tooltip--start"
        : hoveredIndex === dailySearchTrend.length - 1
            ? " pf-search-activity-chart__tooltip--end"
            : "";
    const lineTooltipId = `pf-search-line-tooltip-${compact ? "compact" : "full"}-${days}`;
    const clearLineHover = () => {
        setHoveredIndex(null);
        setHoveredSeries(null);
    };
    const updateLineHover = (
        event: ReactMouseEvent<HTMLDivElement>,
        index: number,
        hasComparison: boolean
    ) => {
        setHoveredIndex(index);

        const bounds = event.currentTarget.getBoundingClientRect();
        const chartHeight = Math.max(1, bounds.height - 24);
        const pointerY = Math.min(chartHeight, Math.max(0, event.clientY - bounds.top));
        const positionWithinColumn = Math.min(
            1,
            Math.max(0, (event.clientX - bounds.left) / Math.max(1, bounds.width))
        );
        const currentHeight = getInterpolatedChartHeight(
            dailySearchTrend,
            index,
            positionWithinColumn,
            axisScale.maximum
        );
        const comparisonHeight = getInterpolatedChartHeight(
            comparisonSearchTrend,
            index,
            positionWithinColumn,
            axisScale.maximum
        );
        const currentY = currentHeight == null
            ? 0
            : chartHeight * (1 - currentHeight / 100);
        const currentDistance = currentHeight == null
            ? Number.POSITIVE_INFINITY
            : Math.abs(pointerY - currentY);
        const comparisonY = comparisonHeight == null
            ? 0
            : chartHeight * (1 - comparisonHeight / 100);
        const comparisonDistance = hasComparison && comparisonHeight != null
            ? Math.abs(pointerY - comparisonY)
            : Number.POSITIVE_INFINITY;
        const focusThreshold = 14;

        if (Math.min(currentDistance, comparisonDistance) > focusThreshold) {
            setHoveredSeries(null);
            return;
        }

        setHoveredSeries(comparisonDistance < currentDistance ? "comparison" : "current");
    };

    const chart = (
        <div
            className={`pf-search-activity-chart pf-search-activity-chart--${variant}${compact ? " pf-search-activity-chart--compact" : ""}${chartDensityClass}${comparisonVisible ? " pf-search-activity-chart--comparison" : ""}${hoveredSeries ? ` pf-search-activity-chart--focus-${hoveredSeries}` : ""}`}
            role="group"
            aria-label={`Daily storefront vehicle search volume for the most recent ${days} days${comparisonVisible ? " with comparison period" : ""}`}
        >
            <div className="pf-search-activity-chart__y-axis" aria-hidden="true">
                {[...axisScale.ticks].reverse().map((tick) => (
                    <span key={tick}>{formatSearchCount(tick)}</span>
                ))}
            </div>

            <div
                className="pf-search-activity-chart__plot"
                onMouseLeave={isLineChart ? clearLineHover : undefined}
            >
                <div className="pf-search-activity-chart__grid" aria-hidden="true">
                    {[...axisScale.ticks].reverse().map((tick) => (
                        <div className="pf-search-activity-chart__gridline" key={tick} />
                    ))}
                </div>

                {isLineChart && (
                    <svg
                        className="pf-search-activity-chart__lines"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        {comparisonVisible && comparisonLinePath && (
                            <path
                                className="pf-search-activity-chart__line pf-search-activity-chart__line--comparison"
                                d={comparisonLinePath}
                            />
                        )}
                        {currentLinePath && (
                            <path
                                className="pf-search-activity-chart__line pf-search-activity-chart__line--current"
                                d={currentLinePath}
                            />
                        )}
                        {inProgressLinePath && <path
                            className="pf-search-activity-chart__line pf-search-activity-chart__line--current"
                            d={inProgressLinePath} strokeDasharray="4 4" />}
                    </svg>
                )}

                <div
                    className="pf-search-activity-chart__columns"
                    style={columnsStyle}
                >
                    {dailySearchTrend.map((day, index) => {
                        const comparisonDay = comparisonSearchTrend[index];
                        const height = axisScale.maximum === 0 || !day.available
                            ? 0
                            : (day.count / axisScale.maximum) * 100;
                        const comparisonHeight = axisScale.maximum === 0 || !comparisonDay?.available
                            ? 0
                            : (comparisonDay.count / axisScale.maximum) * 100;
                        const tooltipHeight = Math.max(height, comparisonHeight);
                        const showDateLabel = compact
                            || index === 0
                            || index === dailySearchTrend.length - 1
                            || index % xAxisLabelInterval === 0;
                        const tooltipAlignment = index === 0
                            ? " pf-search-activity-chart__tooltip--start"
                            : index === dailySearchTrend.length - 1
                                ? " pf-search-activity-chart__tooltip--end"
                                : "";
                        const tooltipId = `pf-search-tooltip-${compact ? "compact" : "full"}-${day.date}`;
                        const columnStyle = {
                            "--pf-chart-tooltip-height": `${tooltipHeight}%`,
                        } as CSSProperties;
                        const currentBarStyle = {
                            "--pf-chart-bar-height": `${height}%`,
                        } as CSSProperties;
                        const comparisonBarStyle = {
                            "--pf-chart-bar-height": `${comparisonHeight}%`,
                        } as CSSProperties;

                        return (
                            <div
                                className="pf-search-activity-chart__column"
                                key={day.date}
                                aria-label={`${day.label}: ${day.available ? `${formatSearchCount(day.count)} searches` : "no data"}${day.date === inProgressDate ? '; day in progress' : ''}${comparisonDay ? `; comparison ${comparisonDay.label}: ${comparisonDay.available ? `${formatSearchCount(comparisonDay.count)} searches` : "no data"}` : ""}`}
                                aria-describedby={isLineChart ? lineTooltipId : tooltipId}
                                style={columnStyle}
                                tabIndex={0}
                                onMouseEnter={isLineChart ? () => {
                                    setHoveredIndex(index);
                                    setHoveredSeries(null);
                                } : undefined}
                                onMouseMove={isLineChart ? (event) => updateLineHover(
                                    event,
                                    index,
                                    Boolean(comparisonDay?.available)
                                ) : undefined}
                                onFocus={isLineChart ? () => {
                                    setHoveredIndex(index);
                                    setHoveredSeries(null);
                                } : undefined}
                                onBlur={isLineChart ? clearLineHover : undefined}
                            >
                                <div className="pf-search-activity-chart__bar-area">
                                    {!isLineChart && (
                                        <>
                                            {comparisonDay?.available && (
                                                <div
                                                    className={`pf-search-activity-chart__bar pf-search-activity-chart__bar--comparison${comparisonDay.count === 0 ? " pf-search-activity-chart__bar--empty" : ""}`}
                                                    style={comparisonBarStyle}
                                                    aria-hidden="true"
                                                />
                                            )}
                                            {day.available && (
                                                <div
                                                    className={`pf-search-activity-chart__bar${day.count === 0 ? " pf-search-activity-chart__bar--empty" : ""}`}
                                                    style={currentBarStyle}
                                                />
                                            )}
                                        </>
                                    )}
                                    {!isLineChart && (
                                        <div
                                            className={`pf-search-activity-chart__tooltip${tooltipAlignment}`}
                                            id={tooltipId}
                                            role="tooltip"
                                        >
                                            <span className="pf-search-activity-chart__tooltip-date">
                                                {formatSearchTooltipDate(day.date)}
                                            </span>
                                            <span className="pf-search-activity-chart__tooltip-value">
                                                <strong>{day.available ? formatSearchCount(day.count) : "No data"}</strong>
                                            </span>
                                            {comparisonDay && (
                                                <>
                                                    <span className="pf-search-activity-chart__tooltip-date">
                                                        Compared with {formatSearchTooltipDate(comparisonDay.date)}
                                                    </span>
                                                    <span className="pf-search-activity-chart__tooltip-value">
                                                        <strong>
                                                            {comparisonDay.available
                                                                ? formatSearchCount(comparisonDay.count)
                                                                : "No data"}
                                                        </strong>
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <span className="pf-search-activity-chart__label">
                                    {showDateLabel ? day.label : ""}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {isLineChart && hoveredDay && hoverIndicatorStyle && (
                    <div
                        className="pf-search-activity-chart__line-hover"
                        style={hoverIndicatorStyle}
                    >
                        <span className="pf-search-activity-chart__hover-guide" aria-hidden="true" />
                        {hoveredComparisonDay?.available && (
                            <span className="pf-search-activity-chart__point pf-search-activity-chart__point--comparison" aria-hidden="true" />
                        )}
                        {hoveredDay.available && (
                            <span className="pf-search-activity-chart__point pf-search-activity-chart__point--current" aria-hidden="true" />
                        )}
                        <div
                            className={`pf-search-activity-chart__tooltip pf-search-activity-chart__tooltip--line${lineTooltipAlignment}`}
                            id={lineTooltipId}
                            role="tooltip"
                        >
                            <strong className="pf-search-activity-chart__tooltip-title">
                                Searches
                            </strong>
                            <span className="pf-search-activity-chart__tooltip-series-date">
                                <i className="pf-search-activity-chart__tooltip-dot pf-search-activity-chart__tooltip-dot--current" aria-hidden="true" />
                                {formatSearchTooltipDate(hoveredDay.date)}
                                {hoveredDay.date === inProgressDate && ' · In progress'}
                            </span>
                            <strong className="pf-search-activity-chart__tooltip-count">
                                {hoveredDay.available
                                    ? formatSearchCount(hoveredDay.count)
                                    : "No data"}
                            </strong>
                            {hoveredComparisonDay && (
                                <>
                                    {hoveredComparisonChange && (
                                        <span
                                            className={`pf-search-activity-chart__tooltip-change pf-search-activity-chart__tooltip-change--${hoveredComparisonChange.tone}`}
                                        >
                                            {hoveredComparisonChange.label}
                                        </span>
                                    )}
                                    <span className="pf-search-activity-chart__tooltip-series-date">
                                        <i className="pf-search-activity-chart__tooltip-dot pf-search-activity-chart__tooltip-dot--comparison" aria-hidden="true" />
                                        {formatSearchTooltipDate(hoveredComparisonDay.date)}
                                    </span>
                                    <strong className="pf-search-activity-chart__tooltip-count">
                                        {hoveredComparisonDay.available
                                            ? formatSearchCount(hoveredComparisonDay.count)
                                            : "No data"}
                                    </strong>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    if (embedded) return chart;

    return (
        <Box
            background="bg-surface"
            borderColor="border"
            borderRadius="300"
            borderWidth="025"
            padding={compact ? "300" : "400"}
        >
            <BlockStack gap="300">
                {isLineChart && (
                    <div className="pf-search-activity-chart__summary-row">
                        <div className="pf-search-activity-chart__summary">
                            <span className="pf-search-activity-chart__summary-label">Searches</span>
                            <span className="pf-search-activity-chart__summary-value">
                                {formatSearchCount(visibleSearchTotal)}
                                {overallComparison && (
                                    <span
                                        className={`pf-search-activity-chart__summary-change pf-search-activity-chart__summary-change--${overallComparison.tone}`}
                                        aria-label={overallComparison.accessibleLabel}
                                    >
                                        {overallComparison.direction} {overallComparison.label}
                                    </span>
                                )}
                                {comparisonVisible && !comparisonDataComplete && (
                                    <span className="pf-search-activity-chart__summary-change pf-search-activity-chart__summary-change--neutral">
                                        Partial comparison
                                    </span>
                                )}
                            </span>
                        </div>
                        {comparisonVisible && report && (
                            <div className="pf-search-activity-chart__legend pf-search-activity-chart__legend--line" aria-label="Chart legend">
                                <span>
                                    <i className="pf-search-activity-chart__legend-swatch pf-search-activity-chart__legend-swatch--current" />
                                    {formatSearchLegendDate(report.from)}–{formatSearchLegendDate(report.to)}
                                </span>
                                <span>
                                    <i className="pf-search-activity-chart__legend-swatch pf-search-activity-chart__legend-swatch--comparison" />
                                    {formatSearchLegendDate(report.previousFrom)}–{formatSearchLegendDate(report.previousTo)}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {!isLineChart && comparisonVisible && report && (
                    <div className="pf-search-activity-chart__legend" aria-label="Chart legend">
                        <span>
                            <i className="pf-search-activity-chart__legend-swatch pf-search-activity-chart__legend-swatch--current" />
                            {formatSearchLegendDate(report.from)}–{formatSearchLegendDate(report.to)}
                        </span>
                        <span>
                            <i className="pf-search-activity-chart__legend-swatch pf-search-activity-chart__legend-swatch--comparison" />
                            {formatSearchLegendDate(report.previousFrom)}–{formatSearchLegendDate(report.previousTo)}
                        </span>
                    </div>
                )}
                {chart}
            </BlockStack>
        </Box>
    );
};
